import { request } from "./client";
import type {
  AdminDailyRequestsResponse,
  AdminDailySimilaritiesResponse,
  AdminExportQueriesResponse,
  AdminListQueriesResponse,
  AdminListUsersResponse,
  AdminOverviewResponse,
  AdminQueryFilters,
  AuthResponse,
  ConversationInfo,
  CreateConversationRequest,
  GetConversationResponse,
  ListConversationsResponse,
  LoginRequest,
  LogoutRequest,
  QueryRequest,
  QueryResponse,
  RegisterRequest,
} from "./types";

function queryString(params: Record<string, string | number | undefined>) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "" && value !== 0) {
      qs.set(key, String(value));
    }
  });
  const raw = qs.toString();
  return raw ? `?${raw}` : "";
}

function adminFilterParams(filters: AdminQueryFilters = {}) {
  return {
    "filters.since": filters.since,
    "filters.until": filters.until,
    "filters.user": filters.user,
    "filters.audience": filters.audience && filters.audience !== "all" ? filters.audience : undefined,
    "filters.search": filters.search,
    "filters.low_similarity_threshold": filters.low_similarity_threshold,
  };
}

export const api = {
  auth: {
    register: (body: RegisterRequest) =>
      request<AuthResponse>("/v1/auth/register", { method: "POST", body, auth: false }),
    login: (body: LoginRequest) =>
      request<AuthResponse>("/v1/auth/login", { method: "POST", body, auth: false }),
    logout: (body: LogoutRequest) =>
      request<Record<string, never>>("/v1/auth/logout", { method: "POST", body, auth: false }),
  },
  conversations: {
    list: () =>
      request<ListConversationsResponse>("/v1/conversations", { method: "GET" }).then(
        (r) => r.conversations ?? [],
      ),
    get: (id: string) =>
      request<GetConversationResponse>(`/v1/conversations/${encodeURIComponent(id)}`, {
        method: "GET",
      }),
    create: (body: CreateConversationRequest = {}) =>
      request<ConversationInfo>("/v1/conversations", { method: "POST", body }),
    delete: (id: string) =>
      request<Record<string, never>>(`/v1/conversations/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
  },
  query: (body: QueryRequest, opts: { auth?: boolean } = {}) =>
    request<QueryResponse>("/v1/query", { method: "POST", body, auth: opts.auth ?? true }),
  admin: {
    overview: () =>
      request<AdminOverviewResponse>("/v1/admin/overview", { method: "GET" }),
    users: (params: { page?: number; page_size?: number; search?: string } = {}) =>
      request<AdminListUsersResponse>(
        `/v1/admin/users${queryString({
          page: params.page,
          page_size: params.page_size,
          search: params.search,
        })}`,
        { method: "GET" },
      ),
    queries: (params: { page?: number; page_size?: number; filters?: AdminQueryFilters } = {}) =>
      request<AdminListQueriesResponse>(
        `/v1/admin/queries${queryString({
          page: params.page,
          page_size: params.page_size,
          ...adminFilterParams(params.filters),
        })}`,
        { method: "GET" },
      ),
    dailyRequests: (params: { since?: number; until?: number } = {}) =>
      request<AdminDailyRequestsResponse>(
        `/v1/admin/analytics/daily-requests${queryString(params)}`,
        { method: "GET" },
      ),
    dailySimilarities: (params: { since?: number; until?: number } = {}) =>
      request<AdminDailySimilaritiesResponse>(
        `/v1/admin/analytics/daily-similarities${queryString(params)}`,
        { method: "GET" },
      ),
    exportQueries: (filters: AdminQueryFilters = {}) =>
      request<AdminExportQueriesResponse>("/v1/admin/exports/queries", {
        method: "POST",
        body: { filters },
      }),
  },
};
